import { createContext, useContext } from "react";
import { useParams, useLocation } from "react-router-dom";

export const LangContext = createContext("fr");

export const SUPPORTED_LANGS = ["fr", "en"];
export const DEFAULT_LANG = "fr";

/**
 * Get string value from a multilingual object {fr, en} or plain string.
 */
export function tr(value, lang = DEFAULT_LANG) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value[lang] || value[DEFAULT_LANG] || value.en || value.fr || "";
  }
  return String(value);
}

export function useLang() {
  const { lang } = useParams();
  return SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
}

export function useT() {
  const lang = useLang();
  return (value) => tr(value, lang);
}

export function localizedPath(path, lang) {
  if (path.startsWith("/admin")) return path;
  const clean = path.replace(/^\/(fr|en)(\/|$)/, "/");
  return `/${lang}${clean === "/" ? "" : clean}`;
}

const UI_STRINGS = {
  fr: {
    book: "Réserver une séance", contact: "Contactez-moi", learnMore: "En savoir plus",
    home: "Accueil", services: "Services", gallery: "Galerie", about: "À propos", contactNav: "Contact", blog: "Blog",
    bookCTA: "Réserver", sendMessage: "Envoyer le message", sending: "Envoi…",
    yourName: "Votre nom", email: "Email", phone: "Téléphone", sessionType: "Type de séance", yourMessage: "Votre message",
    chooseOption: "— Choisissez —", messageSent: "Message envoyé !", thanksForTrust: "Merci pour votre confiance. Je vous réponds sous 48h.",
    sendAnother: "Envoyer un autre message",
    capturedMoments: "Moments capturés", allGallery: "Tous", famille: "Famille", couple: "Couple", naissance: "Naissance", grossesse: "Grossesse",
    galleryIntro: "Un aperçu de mes séances. Chaque image raconte une histoire — celle d'une famille, d'un couple ou d'un instant de vie.",
    noPhotos: "Aucune photo dans cette catégorie pour l'instant.",
    bookSession: "Réserver cette séance", workTogether: "Travaillons ensemble",
    aboutLabel: "À propos", contactLabel: "Contact", galleryLabel: "Galerie", servicesLabel: "Services",
    location: "Zone d'intervention", instagram: "Instagram",
    blogIntro: "Conseils photo, coulisses et inspirations.",
    readMore: "Lire la suite", minRead: "min de lecture", backToBlog: "← Retour au blog",
    publishedOn: "Publié le", by: "par", noPosts: "Aucun article publié pour l'instant.",
    relatedPosts: "Vous aimerez aussi",
    pageNotAvailable: "Page indisponible",
  },
  en: {
    book: "Book a session", contact: "Contact me", learnMore: "Learn more",
    home: "Home", services: "Services", gallery: "Gallery", about: "About", contactNav: "Contact", blog: "Blog",
    bookCTA: "Book", sendMessage: "Send message", sending: "Sending…",
    yourName: "Your name", email: "Email", phone: "Phone", sessionType: "Session type", yourMessage: "Your message",
    chooseOption: "— Choose —", messageSent: "Message sent!", thanksForTrust: "Thanks for your trust. I'll get back to you within 48h.",
    sendAnother: "Send another message",
    capturedMoments: "Captured moments", allGallery: "All", famille: "Family", couple: "Couple", naissance: "Newborn", grossesse: "Pregnancy",
    galleryIntro: "A glimpse into my sessions. Each image tells a story — of a family, a couple, or a moment of life.",
    noPhotos: "No photos in this category yet.",
    bookSession: "Book this session", workTogether: "Let's work together",
    aboutLabel: "About", contactLabel: "Contact", galleryLabel: "Gallery", servicesLabel: "Services",
    location: "Coverage area", instagram: "Instagram",
    blogIntro: "Photo tips, behind-the-scenes and inspiration.",
    readMore: "Read more", minRead: "min read", backToBlog: "← Back to blog",
    publishedOn: "Published on", by: "by", noPosts: "No published articles yet.",
    relatedPosts: "You may also like",
    pageNotAvailable: "Page unavailable",
  }
};

export function useUI() {
  const lang = useLang();
  return UI_STRINGS[lang];
}
